import { type FC } from 'react';

import { Info } from '@livechat/design-system-icons';
import { Icon, NavigationTopBarAlert, Tooltip } from '@livechat/design-system-react-components';
import { useDispatch } from 'react-redux';

import { TopBarNotificationType } from 'constants/notifications';
import { navigate, openInNewCard } from 'helpers/routing';
import type { IVisitorLimitExceededProps } from 'interfaces/notification-bar';
import { NotificationsBarActions } from 'store/features/notifications-bar/actions';

import {
  MESSAGES,
  VISITOR_LIMIT_TOOLTIP_HEADER,
  VISITOR_LIMIT_TOOLTIP_INTRO,
  VISITOR_LIMIT_TOOLTIP_LABEL,
  VISITOR_LIMIT_TOOLTIP_ITEMS,
} from './constants';
import { useVisitorLimitExceededData } from './hooks/use-visitor-limit-exceeded-data';
import { useVisitorLimitExceededStorage } from './hooks/use-visitor-limit-exceeded-storage';
import { useVisitorLimitExceededTracking } from './hooks/use-visitor-limit-exceeded-tracking';

import {
  visitorLimitTooltipWrapper,
  visitorLimitTooltipContent,
  visitorLimitTooltipList,
  visitorLimitTooltipTrigger,
} from './styles';

interface Props extends IVisitorLimitExceededProps {
  isVisible: boolean;
}

export const VisitorLimitExceededBar: FC<Props> = ({ isVisible, count = 1 }) => {
  const dispatch = useDispatch();
  const { messagePrefix, ctaLabel, externalCtaUrl } = useVisitorLimitExceededData();
  const { markDismissed } = useVisitorLimitExceededStorage();
  const { trackClicked, trackSkipped, trackTooltipDisplayed } = useVisitorLimitExceededTracking(count, isVisible);

  const handleUpgradeClick = (): void => {
    trackClicked();
    markDismissed(count);
    dispatch(NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.VisitorLimitExceeded));

    if (externalCtaUrl) {
      openInNewCard(externalCtaUrl);
    } else {
      navigate('/subscription');
    }
  };

  const handleSkip = (): void => {
    trackSkipped();
    markDismissed(count);
    dispatch(NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.VisitorLimitExceeded));
  };

  const getMessage = (): string => {
    const columnIndex = Math.min(Math.max(count - 1, 0), 2);

    return MESSAGES[columnIndex];
  };

  const renderTooltipContent = (): JSX.Element => {
    return (
      <div className={visitorLimitTooltipContent}>
        {VISITOR_LIMIT_TOOLTIP_HEADER}
        <br />
        <br />
        {VISITOR_LIMIT_TOOLTIP_INTRO}
        <br />
        {VISITOR_LIMIT_TOOLTIP_LABEL}
        <ul className={visitorLimitTooltipList}>
          {VISITOR_LIMIT_TOOLTIP_ITEMS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  // Multiple spans are used to ensure the message is displayed correctly for smaller screens.
  // It has to support text wrapping despite part of the text is inside a tooltip and icon should behave like in other banners.
  const renderMessageWithTooltip = (): JSX.Element => {
    return (
      <>
        <Icon source={Info} size="medium" data-testid="visitor-limit-info-icon" />
        <span>
          <span style={{ display: 'inline-block' }}>
            <Tooltip
              placement="bottom"
              floatingStrategy="fixed"
              className={visitorLimitTooltipWrapper}
              triggerRenderer={<span className={visitorLimitTooltipTrigger}>{messagePrefix}</span>}
              onOpen={trackTooltipDisplayed}
            >
              {renderTooltipContent()}
            </Tooltip>
          </span>
          &nbsp;{getMessage()}
        </span>
      </>
    );
  };

  return (
    <NavigationTopBarAlert
      kind="warning"
      isVisible={isVisible}
      primaryCta={{
        label: ctaLabel,
        onClick: handleUpgradeClick,
        size: 'compact',
      }}
      closeButton={{
        onClick: handleSkip,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'aria-label': 'Close visitor limits exceeded notification',
      }}
    >
      {renderMessageWithTooltip()}
    </NavigationTopBarAlert>
  );
};
